import { useEffect, useState } from 'react';
import { StatusBar, Style } from '@capacitor/status-bar';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Main from 'apps/main';
import { Capacitor } from '@capacitor/core';
import { usePushNotifications } from './client/hooks/usePushNotifications';
import './App.css';
import './index.css';

// QueryClient défini au niveau module pour rester stable entre les re-renders
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App: React.FC = () => {

  // Initialiser les push notifications
  const { isInitialized, isSupported, error } = usePushNotifications();

  // Fonction pour mettre à jour la StatusBar selon le thème
  const updateStatusBar = async (isDark: boolean) => {
    if (Capacitor.getPlatform() !== 'ios' || !Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await StatusBar.setStyle({
        style: isDark ? Style.Dark : Style.Light
      });

      await StatusBar.setBackgroundColor({
        color: isDark ? '#1f2937' : '#ffffff'
      });

      console.log(`✅ StatusBar mise à jour: ${isDark ? 'dark' : 'light'}`);
    } catch (error) {
      console.error('❌ Erreur mise à jour StatusBar:', error);
    }
  };

  // Initialisation : Détection de plateforme et configuration StatusBar
  useEffect(() => {
    if (Capacitor.getPlatform() === 'ios') {
      // Afficher la status bar
      StatusBar.show();

      // Définir la couleur de fond de la barre de statut pour iOS
      StatusBar.setBackgroundColor({ color: '#ffffff' });

      // S'assurer que le contenu de l'application commence sous la status bar
      StatusBar.setOverlaysWebView({ overlay: true }).then(() => {
        console.log('Overlay de la status bar activé');
      });
    }
  }, []);

    // Initialisation : Détection de plateforme et configuration StatusBar
    useEffect(() => {
        const platform = Capacitor.getPlatform();
        const isNative = Capacitor.isNativePlatform();

        console.log('🔍 Plateforme détectée:', { platform, isNative });

        // Ajouter classes CSS au <html>
        document.documentElement.classList.add(isNative ? 'app-native' : 'app-web', `platform-${platform}`);

        // Configuration initiale StatusBar pour iOS natif
        if (platform === 'ios' && isNative) {
            StatusBar.setOverlaysWebView({ overlay: true });
            StatusBar.show();

            // Synchroniser avec le thème actuel
            const currentMode = localStorage.getItem('mode');
            updateStatusBar(currentMode === 'dark');
        }
    }, []);

  // Vérifier navigation en attente (clic sur notification)
  useEffect(() => {
    const pendingNav = localStorage.getItem('pending_notification_navigation');
    if (pendingNav) {
      localStorage.removeItem('pending_notification_navigation');
      setTimeout(() => {
        window.location.href = pendingNav;
      }, 1000);
    }
  }, []);

    // Écouter les changements de thème
    useEffect(() => {
        // Fonction pour détecter les changements de thème
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'mode') {
                const isDark = e.newValue === 'dark';
                updateStatusBar(isDark);
            }
        };

        // Écouter les changements dans localStorage
        window.addEventListener('storage', handleStorageChange);

        // Observer les mutations du DOM pour détecter les changements de classe
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'class') {
                    const isDark = document.documentElement.classList.contains('dark');
                    updateStatusBar(isDark);
                }
            });
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            observer.disconnect();
        };
    }, []);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID ?? ''}>
      <QueryClientProvider client={queryClient}>
        <Main />
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
};

export default App;
